import { gql } from '@apollo/client';

export const getResponse = gql`
  query getResponse($id: uuid!) {
    response: form_response_by_pk(id: $id) {
      id
      asset
      complete
      responses
      tags(order_by: [{ category: asc }, { name: asc }]) {
        id
        category
        name
      }
    }
  }
`;

export const getFormsForUser = gql`
  query getFormsForUser($user_id: uuid!) {
    forms: user_form(where: { user: { _eq: $user_id } }) {
      id
      course
      createdAt: created_at
      updatedAt: updated_at
      responsesByResponses {
        id
        asset
        complete
        responses
        tags(order_by: [{ category: asc }, { name: asc }]) {
          id
          category
          name
        }
      }
      form: formByForm {
        id
        title
        content
        tags(order_by: [{ category: asc }, { name: asc }]) {
          id
          category
          name
        }
        formQuestions(order_by: { order: asc }) {
          id
          order
          properties
          question: questionByQuestion {
            id
            label
            type
            type
            public
            properties
          }
        }
      }
      formByForm {
        id
        title
        content
        tags(order_by: [{ category: asc }, { name: asc }]) {
          id
          category
          name
        }
        formQuestions(order_by: { order: asc }) {
          id
          order
          properties
          question: questionByQuestion {
            id
            label
            type
            type
            public
            properties
          }
        }
      }
    }
  }
`;

export const getFormsForEvent = gql`
  query getFormsForEvent($event_id: uuid!, $user_id: uuid!) {
    user_form(
      where: {
        _and: [{ user: { _eq: $user_id } }, { course: { _eq: $event_id } }]
      }
    ) {
      id
      course
      createdAt: created_at
      updatedAt: updated_at
      member
      responsesByResponses {
        id
        asset
        complete
        responses
        tags(order_by: [{ category: asc }, { name: asc }]) {
          id
          category
          name
        }
        reports {
          id
          reportId: model_report_id
          modelReport {
            studentId: student_id
          }
        }
      }
      form: formByForm {
        id
        title
        content
        tags(order_by: [{ category: asc }, { name: asc }]) {
          id
          category
          name
        }
        formQuestions(order_by: { order: asc }) {
          id
          order
          properties
          question: questionByQuestion {
            id
            label
            archived
            type
            public
            properties
          }
        }
      }
      formByForm {
        id
        title
        content
        tags(order_by: [{ category: asc }, { name: asc }]) {
          id
          category
          name
        }
        formQuestions(order_by: { order: asc }) {
          id
          order
          properties
          question: questionByQuestion {
            id
            label
            type
            public
            properties
          }
        }
      }
    }
  }
`;

export const getEventsByRange = gql`
  query getEventsByRange($start: timestamptz, $end: timestamptz) {
    event(
      where: {
        _and: [
          {
            _or: [
              { _and: [{ start: { _gte: $start } }, { start: { _lt: $end } }] }
              { _and: [{ end: { _gt: $start } }, { end: { _lte: $end } }] }
            ]
          }
          { status: { _neq: "ARCHIVED" } }
        ]
      }
    ) {
      id
      status
      updated_at
      start
      seats
      seat_price
      end
      detail_id
      date
      type
      created_at
      detail {
        created_at
        id
        title
      }
      resource: event_resource {
        id
        name
      }
      sessions {
        id
        date
        start
        end
        seats
        product
        product_description
        treatment_id
        price: seat_price
        type
      }
      countStudentRegistrations: registrations_aggregate(
        where: {
          _and: [
            { type: { _eq: "STUDENT" } }
            { status: { _neq: "CANCELLED" } }
          ]
        }
      ) {
        aggregate {
          count
        }
      }
      countModelRegistrations: registrations_aggregate(
        where: {
          _and: [
            { type: { _eq: "MODEL" } }
            { status: { _neq: "CANCELLED" } }
            { session_id: { _is_null: false } }
          ]
        }
      ) {
        aggregate {
          count
        }
      }
      registrations(where: { _and: { status: { _neq: "CANCELLED" } } }) {
        id
        type
        enrollment_id
        confirmed
        confirmed_by
        confirmer {
          full_name
          email
          given_name
          user_assets {
            asset: assetByAsset {
              id
              url
            }
          }
        }
        model_session_purchase {
          id
          status
          total
          line_items
          employee {
            full_name
            email
            given_name
            user_assets {
              asset: assetByAsset {
                id
                url
              }
            }
          }
        }
        enrollment {
          metadata
          client_data
        }
        createdAt: created_at
        user {
          id
          name: given_name
          surname: family_name
          email
          labels: user_labels(order_by: { created_at: desc }, limit: 2) {
            id
            description
            color
            label {
              id
              name
            }
          }
        }
        session {
          start
          end
        }
      }
      resources: event_resources(
        where: { type: { _in: ["TUTOR", "ASSISTANT"] } }
      ) {
        id
        type
        user {
          id
          name: given_name
          surname: family_name
          avatar: user_assets(limit: 1, order_by: { created_at: desc }) {
            asset: assetByAsset {
              id
              url
            }
          }
        }
      }
    }
  }
`;

export const getEventsCalendar = gql`
  query getEventsCalendar(
    $start: timestamptz!
    $end: timestamptz!
    $status: String!
  ) {
    events: get_events_with_status(
      args: { period_end: $end, period_start: $start, event_status: $status }
      order_by: { start: asc }
    ) {
      id
      type
      detail {
        id
        title
        attributes
        requires_prescription
      }
      vtct
      vtctSettings
      sessions {
        id
        start
      }
      price: seat_price
      seats
      start
      status
      end
      date
      sell: is_sellable
      title
      resource_id
      resource: event_resource {
        id
        name
      }
      countStudentRegistrations: registrations_aggregate(
        where: {
          _and: [
            { type: { _eq: "STUDENT" } }
            { status: { _neq: "CANCELLED" } }
          ]
        }
      ) {
        aggregate {
          count
        }
      }
      countModelRegistrations: registrations_aggregate(
        where: {
          _and: [
            { type: { _eq: "MODEL" } }
            { status: { _neq: "CANCELLED" } }
            { session_id: { _is_null: false } }
          ]
        }
      ) {
        aggregate {
          count
        }
      }
      students: registrations(
        where: {
          _and: { status: { _neq: "CANCELLED" }, type: { _eq: "STUDENT" } }
        }
      ) {
        id
        type
        enrollment_id
        user {
          id
          name: given_name
          surname: family_name
          labels: user_labels {
            id
            description
            color
            label {
              id
              name
            }
          }
          email
        }
      }
      models: registrations(
        where: {
          _and: { status: { _neq: "CANCELLED" }, type: { _eq: "MODEL" } }
        }
      ) {
        id
        type
        confirmed
        confirmed_by
        confirmer {
          full_name
          email
          given_name
          user_assets {
            asset: assetByAsset {
              id
              url
            }
          }
        }
        session {
          start
          end
        }
        user {
          id
          name: given_name
          surname: family_name
          labels: user_labels {
            id
            description
            color
            label {
              id
              name
            }
          }
          email
        }
      }
      resources: event_resources(
        where: { type: { _in: ["TUTOR", "ASSISTANT"] } }
      ) {
        id
        type
        user {
          id
          name: given_name
          surname: family_name
          avatar: user_assets(
            where: { assetByAsset: { type: { _eq: AVATAR } } }
            limit: 1
            order_by: { created_at: desc }
          ) {
            asset: assetByAsset {
              id
              url
            }
          }
        }
      }
    }
  }
`;

/** Calendar payload for sales_agent - seats + tutors (resources + vtctSettings). */
export const getSalesAgentEventsCalendar = gql`
  query getSalesAgentEventsCalendar(
    $start: timestamptz!
    $end: timestamptz!
    $status: String!
  ) {
    events: get_events_with_status(
      args: { period_end: $end, period_start: $start, event_status: $status }
      order_by: { start: asc }
    ) {
      id
      type
      detail {
        id
        title
        attributes
        requires_prescription
      }
      vtct
      vtctSettings
      sessions {
        id
        start
      }
      price: seat_price
      seats
      start
      status
      end
      date
      sell: is_sellable
      title
      resource_id
      countStudentRegistrations: registrations_aggregate(
        where: {
          _and: [
            { type: { _eq: "STUDENT" } }
            { status: { _neq: "CANCELLED" } }
          ]
        }
      ) {
        aggregate {
          count
        }
      }
      countModelRegistrations: registrations_aggregate(
        where: {
          _and: [
            { type: { _eq: "MODEL" } }
            { status: { _neq: "CANCELLED" } }
            { session_id: { _is_null: false } }
          ]
        }
      ) {
        aggregate {
          count
        }
      }
      students: registrations(
        where: {
          _and: { status: { _neq: "CANCELLED" }, type: { _eq: "STUDENT" } }
        }
      ) {
        id
        type
        enrollment_id
        user {
          id
          name: given_name
          surname: family_name
          email
        }
      }
      resources: event_resources(
        where: { type: { _in: ["TUTOR", "ASSISTANT"] } }
      ) {
        id
        type
        user {
          id
          name: given_name
          surname: family_name
          avatar: user_assets(
            where: { assetByAsset: { type: { _eq: AVATAR } } }
            limit: 1
            order_by: { created_at: desc }
          ) {
            asset: assetByAsset {
              id
              url
            }
          }
        }
      }
    }
  }
`;

// Fetched only when a calendar event is opened. Keep registrations as counts so
// opening the summary does not load students' records or the full event editor.
const courseCalendarEventSummaryFields = gql`
  fragment CourseCalendarEventSummaryFields on event {
    id
    title
    type
    status
    start
    end
    seats
    resource_id
    vtctSettings
    detail {
      id
      title
      attributes
    }
    countStudentRegistrations: registrations_aggregate(
      where: { type: { _eq: "STUDENT" }, status: { _neq: "CANCELLED" } }
    ) {
      aggregate {
        count
      }
    }
    countModelRegistrations: registrations_aggregate(
      where: {
        type: { _eq: "MODEL" }
        status: { _neq: "CANCELLED" }
        session_id: { _is_null: false }
      }
    ) {
      aggregate {
        count
      }
    }
    sessions(order_by: { start: asc }) {
      id
      type
      product
      date
      start
      end
      seats
      seatsFilled: registrations_aggregate(
        where: { type: { _eq: "MODEL" }, status: { _neq: "CANCELLED" } }
      ) {
        aggregate {
          count
        }
      }
    }
    resources: event_resources(
      where: { type: { _in: ["TUTOR", "ASSISTANT"] } }
    ) {
      id
      type
      user {
        id
        name: given_name
        surname: family_name
        avatar: user_assets(
          where: { assetByAsset: { type: { _eq: AVATAR } } }
          limit: 1
          order_by: { created_at: desc }
        ) {
          asset: assetByAsset {
            id
            url
          }
        }
      }
    }
  }
`;

export const getCourseCalendarEventSummary = gql`
  query getCourseCalendarEventSummary($id: uuid!) {
    event: event_by_pk(id: $id) {
      ...CourseCalendarEventSummaryFields
      resource: event_resource {
        id
        name
      }
    }
  }
  ${courseCalendarEventSummaryFields}
`;

// sales_agent can read event sessions and counts, but not the room name field.
export const getCourseCalendarEventSummaryForSales = gql`
  query getCourseCalendarEventSummaryForSales($id: uuid!) {
    event: event_by_pk(id: $id) {
      ...CourseCalendarEventSummaryFields
    }
  }
  ${courseCalendarEventSummaryFields}
`;

export const getSchedulingPlannerAvailability = gql`
  query getSchedulingPlannerAvailability(
    $eventStart: timestamptz!
    $eventEnd: timestamptz!
    $scheduleStart: date!
    $scheduleEnd: date!
  ) {
    events: get_events_with_status(
      args: {
        period_end: $eventEnd
        period_start: $eventStart
        event_status: "NEW"
      }
      order_by: { start: asc }
    ) {
      id
      title
      start
      end
      resource_id
      vtct
      vtctSettings
      detail {
        id
        title
      }
      resources: event_resources(
        where: { type: { _in: ["TUTOR", "ASSISTANT"] } }
      ) {
        id
        type
        user {
          id
        }
      }
    }
    schedule(
      where: {
        _and: [
          { date: { _gte: $scheduleStart } }
          { date: { _lte: $scheduleEnd } }
        ]
      }
      order_by: [{ date: asc }, { start: asc_nulls_last }]
    ) {
      id
      available
      comment
      date
      end
      is_leave
      start
      type
      user
    }
    roomClosures: staff_closure(
      where: {
        status: { _eq: "ACTIVE" }
        resource_id: { _is_null: false }
        start_date: { _lte: $scheduleEnd }
        end_date: { _gte: $scheduleStart }
      }
    ) {
      id
      kind
      title
      start_date
      end_date
      day_part
      resource_id
    }
  }
`;

export const getProgrammeEvents = gql`
  query getEvents($where: event_bool_exp) {
    events: event(where: $where, order_by: { start: asc }) {
      id
      detail {
        id
        title
        attributes
      }
      detail_id
      vtct
      vtctSettings
      price: seat_price
      seat_price
      seats
      start
    }
  }
`;

export const getEventsSlim = gql`
  query getEvents($where: event_bool_exp, $limit: Int = 1000) {
    events: event(where: $where, order_by: { start: asc }, limit: $limit) {
      id
      detail {
        id
        title
      }
      price: seat_price
      seat_price
      seats
      start
      status
      end
      detail_id
      eventDetailId: detail_id
      date
      type
      sell: is_sellable
      cohort: cohort_id
      title
      createdAt: created_at
      updatedAt: updated_at
      resource: event_resource {
        id
        name
      }
      countStudentRegistrations: registrations_aggregate(
        where: {
          _and: [
            { type: { _eq: "STUDENT" } }
            { status: { _neq: "CANCELLED" } }
          ]
        }
      ) {
        aggregate {
          count
        }
      }
    }
  }
`;

export const getEvents = gql`
  query getEvents($where: event_bool_exp, $limit: Int = 1000) {
    events: event(where: $where, order_by: { start: asc }, limit: $limit) {
      id
      detail {
        id
        title
        attributes
        contentfulId: contentful_id
        createdAt: created_at
        metadata
        updatedAt: updated_at
        standardSeats: standard_seat
        standardPrice: standard_price
      }
      vtct
      vtctSettings
      price: seat_price
      seat_price
      seats
      start
      status
      end
      detail_id
      eventDetailId: detail_id
      date
      type
      sell: is_sellable
      cohort: cohort_id
      title
      createdAt: created_at
      updatedAt: updated_at
      resource: event_resource {
        id
        name
      }
      sessions {
        id
        date
        event_id
        start
        end
        seats
        product
        product_description
        treatment_id
        prescription
        treatment {
          id
          prescription
        }
        seat_price
        price: seat_price
        type
      }
      countStudentRegistrations: registrations_aggregate(
        where: {
          _and: [
            { type: { _eq: "STUDENT" } }
            { status: { _neq: "CANCELLED" } }
          ]
        }
      ) {
        aggregate {
          count
        }
      }
      countModelRegistrations: registrations_aggregate(
        where: {
          _and: [
            { type: { _eq: "MODEL" } }
            { status: { _neq: "CANCELLED" } }
            { session_id: { _is_null: false } }
          ]
        }
      ) {
        aggregate {
          count
        }
      }
      students: registrations(
        where: {
          _and: { status: { _neq: "CANCELLED" }, type: { _eq: "STUDENT" } }
        }
      ) {
        id
        createdAt: created_at
        enrollment {
          id
        }
        user {
          id
          name: given_name
          surname: family_name
          email
        }
      }
      models: registrations(
        where: {
          _and: { status: { _neq: "CANCELLED" }, type: { _eq: "MODEL" } }
        }
      ) {
        id
        user_id
        session_id
        createdAt: created_at
        user {
          id
          name: given_name
          surname: family_name
          contact_number
          email
        }
      }
      resources: event_resources(
        where: { type: { _in: ["TUTOR", "ASSISTANT"] } }
      ) {
        id
        type
        user {
          id
          name: given_name
          surname: family_name
          avatar: user_assets(limit: 1, order_by: { created_at: desc }) {
            asset: assetByAsset {
              id
              url
            }
          }
        }
      }
    }
  }
`;

export const getEventForExamScheduling = gql`
  query getEventForExamScheduling($id: uuid!) {
    event: event_by_pk(id: $id) {
      id
      detail {
        title
        programme_id
        programme {
          id
          title
          active
          itec
          units {
            id
            title
            unit_code
            hasExam
          }
        }
      }
      vtctSettings
      studentRegistrations: registrations(
        where: {
          _and: {
            event_id: { _eq: $id }
            status: { _neq: "CANCELLED" }
            type: { _eq: "STUDENT" }
          }
        }
      ) {
        id
        user {
          id
          given_name
          family_name
          email
        }
      }
    }
  }
`;

export const getEventById = gql`
  query getEventById($id: uuid!) {
    event: event_by_pk(id: $id) {
      status
      updated_at
      start
      seats
      vtct
      vtctSettings
      seat_price
      end
      detail_id
      date
      type
      created_at
      resource_id
      cohorts {
        id
      }
      video_calls(where: { status: { _eq: "confirmed" } }) {
        id
        created_at
        updated_at
        url
        meet_id
        status
        start
        end
        data
      }
      resource: event_resource {
        id
        name
      }
      detail {
        created_at
        metadata
        attributes
        id
        standard_price
        title
        updated_at
        standard_seat
        programme_id
        programme {
          title
          units {
            id
            title
          }
        }
      }
      id
      countStudentRegistrations: registrations_aggregate(
        where: {
          _and: {
            event_id: { _eq: $id }
            status: { _neq: "CANCELLED" }
            type: { _eq: "STUDENT" }
          }
        }
      ) {
        aggregate {
          count
        }
      }
      studentRegistrations: registrations(
        where: {
          _and: {
            event_id: { _eq: $id }
            status: { _neq: "CANCELLED" }
            type: { _eq: "STUDENT" }
          }
        }
      ) {
        id
        session_id
        status
        type
        updated_at
        user_id
        student_id
        event_id
        created_at
        enrollment_id
        exam_sessions {
          id
          created_at
          updated_at
          meet_id
          status
          start
          end
          data
          session
          member {
            id
            full_name
            email
            given_name
            user_assets {
              asset: assetByAsset {
                id
                url
              }
            }
          }
        }
        enrollment {
          id
          balance
          status
          total
          client_data
          metadata
          deposit
        }
        user {
          created_at
          name: given_name
          surname: family_name
          given_name
          family_name
          email
          id
          contact_number
          cohort_members(where: { event_id: { _eq: $id } }) {
            id
          }
          labels: user_labels {
            id
            description
            color
            label {
              id
              name
            }
          }
          user_forms(order_by: { created_at: desc }) {
            id
            course
            session_id
            created_at
            responsesByResponses {
              id
              asset
              complete
              responses
              reports {
                reportId: model_report_id
              }
            }
            form: formByForm {
              id
              title
              content
              formQuestions(order_by: { order: asc }) {
                id
                order
                properties
                archived
                question: questionByQuestion {
                  id
                  label
                  archived
                  type
                  public
                  properties
                }
              }
            }
            formByForm {
              id
              title
              content
              formQuestions(order_by: { order: asc }) {
                id
                order
                properties
                archived
                question: questionByQuestion {
                  id
                  label
                  type
                  public
                  archived
                  properties
                }
              }
            }
          }
          certificates {
            event_id
            id
            created_at
          }
        }
        invoices {
          id
          invoice_id
          amount
          status
        }
        payments {
          payment {
            created_at
            amount
            discount
            discount_id
            discount_explanation
            method
          }
        }
      }
      countModelRegistrations: registrations_aggregate(
        where: {
          _and: {
            event_id: { _eq: $id }
            status: { _neq: "CANCELLED" }
            _or: [{ type: { _eq: "MODEL" } }, { type: { _eq: "NO_SHOW" } }]
          }
        }
      ) {
        aggregate {
          count
        }
      }
      modelRegistrations: registrations(
        where: {
          _and: {
            event_id: { _eq: $id }
            status: { _neq: "CANCELLED" }
            _or: [{ type: { _eq: "MODEL" } }, { type: { _eq: "NO_SHOW" } }]
          }
        }
      ) {
        id
        session_id
        status
        type
        confirmed
        confirmed_by
        follow_up
        updated_at
        user_id
        event_id
        created_at
        student_id
        confirmer {
          full_name
          email
          given_name
          user_assets {
            asset: assetByAsset {
              id
              url
            }
          }
        }
        model_session_purchase {
          id
          status
          total
          line_items
          employee {
            full_name
            email
            given_name
            user_assets {
              asset: assetByAsset {
                id
                url
              }
            }
          }
        }
        modelReports {
          status
          id
          student_id
          student {
            id
            name: given_name
            surname: family_name
          }
          created_at
          updated_at
          assets {
            id
            asset {
              props
              url
            }
          }
        }
        user {
          created_at
          given_name
          family_name
          name: given_name
          surname: family_name
          email
          contact_number
          id
          labels: user_labels {
            id
            description
            color
            label {
              id
              name
            }
          }
          user_forms {
            id
            course
            session_id
            created_at
            responsesByResponses {
              id
              responses
              asset
              complete
              completed_by_employee
              completed_at
              client_complete
              client_completed_at
              reports {
                reportId: model_report_id
              }
            }
            form: formByForm {
              id
              title
              content
              formQuestions(order_by: { order: asc }) {
                id
                order
                properties
                archived
                question: questionByQuestion {
                  id
                  label
                  type
                  public
                  archived
                  properties
                }
              }
            }
            formByForm {
              id
              title
              content
              formQuestions(order_by: { order: asc }) {
                id
                order
                properties
                archived
                question: questionByQuestion {
                  id
                  label
                  type
                  archived
                  public
                  properties
                }
              }
            }
          }
          memberships(
            where: { status: { _in: ["ACTIVE", "SUSPENDED"] } }
            limit: 1
          ) {
            id
            status
            plan {
              name
            }
          }
        }
        payments {
          payment {
            created_at
            amount
            discount
            discount_id
            discount_explanation
            method
          }
        }
      }
      sessions(
        where: { archived_at: { _is_null: true } }
        order_by: { start: asc }
      ) {
        updated_at
        seat_price
        start
        seats
        type
        product
        product_description
        treatment_id
        prescription
        treatment {
          id
          prescription
        }
        membership_access
        membership_eligible
        membership_member_release_at
        membership_public_release_at
        membership_max_member_spaces
        membership_max_public_spaces
        membership_last_minute_hours
        membership_bypass_booking_limit
        membership_segment_tag
        membership_model_criteria
        membership_screening_required
        model_criteria_structured
        id
        end
        auto
        date
        created_at
        event_id
        registration
        updated_by
        created_by
        updatedBy {
          id
          created_at
          name: given_name
          surname: family_name
        }
        createdBy {
          id
          created_at
          name: given_name
          surname: family_name
        }
        registrations_aggregate(
          where: { type: { _neq: "CANCELLED" }, status: { _neq: "CANCELLED" } }
        ) {
          aggregate {
            count
          }
        }
      }
      event_resources(order_by: { created_at: asc }) {
        user_id
        type
        id
        event_id
        user {
          given_name
          family_name
          email
          user_assets(
            limit: 1
            order_by: { assetByAsset: { createdAt: desc } }
          ) {
            assetByAsset {
              url
            }
          }
        }
      }
      event_notes {
        data: noteByNote {
          created_at
          content
          user
          title
          updated_at
          id
        }
        note
        id
        created_at
        event
        updated_at
      }
    }
  }
`;

export const getAllEventsById = gql`
  query getEventById($id: [uuid!]) {
    event(where: { id: { _in: $id } }) {
      id
      created_at
      updated_at
      start
      end
      seats
      seat_price
      status
      type
      detail {
        title
      }
    }
  }
`;

export const insertEvent = gql`
  mutation insertEvent($data: event_insert_input!) {
    insert_event_one(object: $data) {
      id
    }
  }
`;

export const insertEvents = gql`
  mutation insertEvents($data: [event_insert_input!]!) {
    insert_event(objects: $data) {
      returning {
        id
      }
    }
  }
`;

export const getAllEventDetailsExcludingPackages = gql`
  query getAllEventDetails {
    event_detail(
      where: {
        _and: [{ archived: { _neq: true } }, { is_package: { _eq: false } }]
      }
      order_by: { title: asc }
    ) {
      id
      title
      standard_price
      standard_seat
      updated_at
      created_at
      contentful_id
      metadata
      type
      archived
      wasPrice: was_price
      is_package
      events(
        where: { status: { _neq: "ARCHIVED" } }
        order_by: { start: asc }
      ) {
        id
        date
        start
        end
        seats
        seat_price
        status
        type
        detail_id
        detail {
          title
        }
        event_resources {
          id
          user_id
          type
          user {
            given_name
          }
        }
      }
    }
  }
`;

export const insertMaterial = gql`
  mutation insertMaterial($data: [event_material_insert_input!]!) {
    insert_event_material(objects: $data) {
      affected_rows
    }
  }
`;

export const deleteMaterials = gql`
  mutation deleteMaterials($ids: [uuid!]!) {
    delete_event_material(where: { id: { _in: $ids } }) {
      affected_rows
    }
  }
`;

export const deleteMaterial = gql`
  mutation deleteMaterial($eventId: uuid!) {
    delete_event_material(where: { event_detail_id: { _eq: $eventId } }) {
      affected_rows
    }
  }
`;

export const getAllEventDetails = gql`
  query getAllEventDetails {
    event_detail(
      where: { archived: { _neq: true } }
      order_by: { title: asc, is_package: desc }
    ) {
      id
      title
      standard_price
      standard_seat
      assets
      updated_at
      created_at
      contentful_id
      metadata
      type
      archived
      elearning_course_id
      elearning_course_ids
      programme_id
      programme {
        id
        title
        active
      }
      wasPrice: was_price
      attributes
      is_package
      event_detail_treatments {
        id
        treatment_id
        treatment {
          id
          title
          option
          description
          price
        }
      }
      materials {
        id
        created_at
        source
        attributes
        name
      }
      template {
        id
        created_at
        updated_at
        template {
          id
          created_at
          updated_at
          label
          groups(
            order_by: [{ position: asc_nulls_last }, { created_at: asc }]
          ) {
            id
            created_at
            updated_at
            label
            position
            sections(
              order_by: [{ position: asc_nulls_last }, { created_at: asc }]
            ) {
              id
              resource_id
              created_at
              updated_at
              position
              label
              start
              end
              sessions
            }
          }
        }
      }
    }
  }
`;

export const insertEventDetailTreatment = gql`
  mutation insertEventDetailTreatment(
    $event_detail_id: uuid!
    $treatment_id: uuid!
  ) {
    insert_event_detail_treatment_one(
      object: { event_detail_id: $event_detail_id, treatment_id: $treatment_id }
      on_conflict: {
        constraint: event_detail_treatment_event_detail_id_treatment_id_key
        update_columns: []
      }
    ) {
      id
    }
  }
`;

export const deleteEventDetailTreatment = gql`
  mutation deleteEventDetailTreatment($id: uuid!) {
    delete_event_detail_treatment_by_pk(id: $id) {
      id
    }
  }
`;

export const getAllEventDetailsSlim = gql`
  query getAllEventDetails {
    event_detail(
      where: {
        _and: [{ archived: { _neq: true } }, { is_package: { _eq: false } }]
      }
      order_by: { title: asc, is_package: desc }
    ) {
      id
      title
      attributes
      requires_prescription
    }
  }
`;

/** Catalog for pickers/filters - no nested admin graph (programme/templates/materials). */
export const getEventDetailCatalog = gql`
  query getEventDetailCatalog {
    event_detail(
      where: { archived: { _neq: true } }
      order_by: { title: asc, is_package: desc }
    ) {
      id
      title
      attributes
      is_package
      standard_price
      wasPrice: was_price
      requires_prescription
    }
  }
`;

export const insertEventSession = gql`
  mutation insertEventSession($data: event_session_insert_input!) {
    insert_event_session_one(object: $data) {
      id
      start
      end
    }
  }
`;
export const insertEventSessions = gql`
  mutation insertEventSession($data: [event_session_insert_input!]!) {
    insert_event_session(objects: $data) {
      affected_rows
    }
  }
`;

export const updateEvent = gql`
  mutation updateEvent($id: uuid!, $data: event_set_input!) {
    update_event_by_pk(pk_columns: { id: $id }, _set: $data) {
      id
      date
      start
      end
      detail {
        title
      }
    }
  }
`;

export const updateEventSession = gql`
  mutation updateEventSession($id: uuid!, $data: event_session_set_input!) {
    update_event_session_by_pk(pk_columns: { id: $id }, _set: $data) {
      id
    }
  }
`;

export const deleteEventSession = gql`
  mutation deleteEventSession($id: uuid!) {
    delete_event_session_by_pk(id: $id) {
      id
      date
      start
      end
    }
  }
`;

export const removeModelSession = gql`
  mutation RemoveModelSession($id: uuid!) {
    remove_model_session(args: { session_id: $id }) {
      id
      date
      start
      end
      archived_at
    }
  }
`;

export const modelSessionsInFutureByEvent = gql`
  query modelSessionsInFutureByEvent(
    $now: timestamptz!
    $detailId: uuid!
    $treatmentId: uuid
    $productLike: String!
  ) {
    event_session(
      where: {
        _and: [
          { seats: { _gt: 0 } }
          {
            _or: [
              { start: { _gte: $now } }
              {
                _and: [{ start: { _is_null: true } }, { date: { _gte: $now } }]
              }
            ]
          }
          { event: { status: { _neq: "ARCHIVED" } } }
          {
            _or: [
              { event: { detail_id: { _eq: $detailId } } }
              { treatment_id: { _eq: $treatmentId } }
              { product: { _ilike: $productLike } }
              {
                event: {
                  detail: {
                    event_detail_treatments: {
                      treatment_id: { _eq: $treatmentId }
                    }
                  }
                }
              }
            ]
          }
        ]
      }
      order_by: { start: asc }
    ) {
      id
      created_at
      updated_at
      type
      event_id
      product
      product_description
      treatment_id
      treatment {
        id
        title
        option
      }
      event {
        id
        start
        end
        detail {
          id
          title
          type
        }
        event_resources {
          id
          type
          user {
            given_name
          }
        }
      }
      date
      start
      end
      seats
      seat_price
      available
      seatsFilled: registrations_aggregate(
        where: {
          _and: { status: { _neq: "CANCELLED" }, type: { _eq: "MODEL" } }
        }
      ) {
        aggregate {
          count
        }
      }
    }
  }
`;

export const getAllEventSessionsById = gql`
  query getAllEventSessionsById($id: [uuid!]!) {
    eventSession: event_session(where: { id: { _in: $id } }) {
      id
      created_at
      updated_at
      start
      end
      seats
      seat_price
      registrations {
        id
        created_at
        status
        type
        user {
          id
          name: given_name
          surname: family_name
        }
        event {
          id
          detail {
            title
          }
        }
      }
    }
  }
`;

export const insertEventResource = gql`
  mutation insertEventResource($data: event_resource_insert_input!) {
    insert_event_resource_one(object: $data) {
      id
    }
  }
`;
export const insertEventResources = gql`
  mutation insertEventResource($data: [event_resource_insert_input!]!) {
    insert_event_resource(objects: $data) {
      affected_rows
    }
  }
`;

export const deleteEventResources = gql`
  mutation deleteEventResource($where: event_resource_bool_exp!) {
    delete_event_resource(where: $where) {
      affected_rows
    }
  }
`;

export const deleteEventResource = gql`
  mutation deleteEventResource($id: uuid!) {
    delete_event_resource_by_pk(id: $id) {
      id
    }
  }
`;

export const addEventNote = gql`
  mutation eventNote($data: event_note_insert_input!) {
    insert_event_note_one(object: $data) {
      id
      note: noteByNote {
        id
        content
        createdAt: created_at
        updatedAt: updated_at
        user
      }
    }
  }
`;

export const deleteNote = gql`
  mutation deleteNote($id: uuid!) {
    delete_note_by_pk(id: $id) {
      id
    }
  }
`;

export const insertEventDetail = gql`
  mutation insertEventDetail($data: event_detail_insert_input!) {
    insert_event_detail_one(object: $data) {
      id
    }
  }
`;

export const updateEventDetail = gql`
  mutation updateEventDetail($id: uuid!, $data: event_detail_set_input!) {
    update_event_detail_by_pk(pk_columns: { id: $id }, _set: $data) {
      id
    }
  }
`;

// export const updateAppointmentNote = gql`
// mutation UpdateNote($id: uuid!, $content: String!) {
//   update_note_by_pk(
//     pk_columns: { id: $id }
//     _set: {
//       content: $content
//     }
//   ) {
//     id
//     title
//     content
//     user
//     updatedAt: updated_at
//   }
// }
// `

export const getFutureEvents = gql`
  query getFutureEvents($date: timestamptz) {
    event(where: { _and: { date: { _gte: $date } } }) {
      status
      updated_at
      start
      seats
      seat_price
      end
      detail_id
      date
      type
      created_at
      detail {
        created_at
        metadata
        id
        standard_price
        title
        updated_at
        standard_seat
      }
    }
  }
`;

export const getResourceById = gql`
  query getResourceById($id: uuid!) {
    resource_by_pk(id: $id) {
      id
      created_at
      updated_at
      name
      teach
    }
  }
`;

export const getResources = gql`
  query getResources {
    resource(order_by: { name: asc }) {
      id
      name
      teach
      created_at
      updated_at
    }
  }
`;

export const getEventStatistics = gql`
  query EvStats($start: timestamptz, $end: timestamptz) {
    get_event_statistics(args: { start_date: $start, end_date: $end }) {
      seat_utilization_percentage
      students_booked_this_month
      total_empty_courses
      total_events_scheduled
      total_full_events
      total_registered_seats
      total_registrations
      total_seats_available
      total_value_of_open_seats
    }
  }
`;

export const insertExamSession = gql`
  mutation insertExamSession($data: exam_session_insert_input!) {
    session: insert_exam_session_one(object: $data) {
      id
    }
  }
`;

export const updateExamSessions = gql`
  mutation updateExamSession(
    $where: exam_session_bool_exp!
    $data: exam_session_set_input!
  ) {
    session: update_exam_session(where: $where, _set: $data) {
      affected_rows
    }
  }
`;
export const updateExamSession = gql`
  mutation updateExamSession($id: uuid!, $data: exam_session_set_input!) {
    session: update_exam_session_by_pk(pk_columns: { id: $id }, _set: $data) {
      id
    }
  }
`;

export const getExams = gql`
  query getExams(
    $where: exam_session_bool_exp
    $order_by: [exam_session_order_by!]
    $limit: Int
    $offset: Int
  ) {
    examSessions: exam_session(
      where: $where
      order_by: $order_by
      limit: $limit
      offset: $offset
    ) {
      id
      created_at
      updated_at
      updated_by
      created_by
      event_id
      registration_id
      data
      meet_id
      start
      attempt
      end
      status
      reexamination_charge {
        id
        status
        payment_id
        waiver_reason
        billing_attempt
      }
      pending_reexamination_charge {
        id
        status
        payment_id
      }
      member_id
      programme_id
      programme_unit_id
      exam_id
      exam {
        id
        title
      }
      programme_unit {
        id
        title
        unit_code
      }
      url
      session
      event {
        id
        start
        end
        detail {
          title
        }
      }
      user {
        id
        name: given_name
        surname: family_name
        full_name
        email
        cohort_members {
          id
          cohort {
            programme {
              id
            }
            event {
              id
            }
          }
        }
      }
      registration {
        id
        status
        user {
          id
          name: given_name
          surname: family_name
          full_name
          email
          cohort_members {
            id
            cohort {
              programme {
                id
              }
              event {
                id
              }
            }
          }
        }
      }
      updatedBy {
        id
        name: given_name
        surname: family_name
        memberAvatar: user_assets {
          asset: assetByAsset {
            id
            url
          }
        }
      }
      createdBy {
        id
        name: given_name
        surname: family_name
        memberAvatar: user_assets {
          asset: assetByAsset {
            id
            url
          }
        }
      }
      member {
        id
        name: given_name
        surname: family_name
        memberAvatar: user_assets {
          asset: assetByAsset {
            id
            url
          }
        }
      }
    }
    examSessionsAggregate: exam_session_aggregate(where: $where) {
      aggregate {
        count
      }
    }
  }
`;

export const getMemberExams = gql`
  query getMemberExams($userId: uuid!, $programmeId: uuid!) {
    exams: exam_session(
      where: {
        _and: [
          {
            _or: [
              { user_id: { _eq: $userId } }
              { registration: { user_id: { _eq: $userId } } }
            ]
          }
          { programme_id: { _eq: $programmeId } }
        ]
      }
      order_by: { created_at: desc }
    ) {
      id
      created_at
      updated_at
      event_id
      registration_id
      data
      attempt
      counts_as_attempt
      meet_id
      start
      end
      status
      member_id
      url
      session
      programme_id
      programme_unit_id
      exam_id
      exam {
        id
        title
      }
      programme_unit {
        id
        title
        unit_code
      }
      event {
        id
        start
        end
        detail {
          title
        }
      }
      user {
        id
        name: given_name
        surname: family_name
        full_name
        email
      }
      registration {
        id
        status
        user {
          id
          name: given_name
          surname: family_name
          full_name
          email
        }
      }
      updatedBy {
        id
        name: given_name
        surname: family_name
        memberAvatar: user_assets {
          asset: assetByAsset {
            id
            url
          }
        }
      }
      createdBy {
        id
        name: given_name
        surname: family_name
        memberAvatar: user_assets {
          asset: assetByAsset {
            id
            url
          }
        }
      }
      member {
        id
        name: given_name
        surname: family_name
        memberAvatar: user_assets {
          asset: assetByAsset {
            id
            url
          }
        }
      }
      exam_result {
        id
        result
        score
        completed_at
        assets: exam_result_assets {
          id
          asset {
            id
            url
            props
          }
        }
      }
    }
  }
`;

export const getExamSessionDetail = gql`
  query getExamSessionDetail($id: uuid!) {
    examSession: exam_session_by_pk(id: $id) {
      id
      created_at
      updated_at
      created_by
      updated_by
      event_id
      registration_id
      data
      meet_id
      start
      attempt
      end
      status
      reexamination_charge {
        id
        status
        payment_id
        waiver_reason
        billing_attempt
      }
      member_id
      programme_id
      programme_unit_id
      exam_id
      exam {
        id
        title
      }
      url
      session
      programme {
        id
        title
        active
        itec
        units(where: { active: { _eq: true } }, order_by: { title: asc }) {
          id
          title
          unit_code
          hasExam
          exams(order_by: { created_at: asc }) {
            id
            title
            active
            is_default
          }
        }
      }
      programme_unit {
        id
        title
        unit_code
      }
      event {
        id
        start
        end
        detail {
          title
          programme_id
          programme {
            id
            title
            active
            units(where: { active: { _eq: true } }, order_by: { title: asc }) {
              id
              title
              unit_code
              hasExam
              exams(order_by: { created_at: asc }) {
                id
                title
                active
                is_default
              }
            }
          }
        }
      }
      user {
        id
        name: given_name
        surname: family_name
        full_name
        email
        cohort_members {
          id
          cohort {
            programme {
              id
            }
            event {
              id
            }
          }
        }
      }
      registration {
        id
        status
        user {
          id
          name: given_name
          surname: family_name
          full_name
          email
          cohort_members {
            id
            cohort {
              programme {
                id
              }
              event {
                id
              }
            }
          }
        }
      }
      updatedBy {
        id
        name: given_name
        surname: family_name
        full_name
        memberAvatar: user_assets(
          where: { assetByAsset: { type: { _eq: AVATAR } } }
        ) {
          asset: assetByAsset {
            id
            url
          }
        }
      }
      createdBy {
        id
        name: given_name
        surname: family_name
        full_name
        memberAvatar: user_assets(
          where: { assetByAsset: { type: { _eq: AVATAR } } }
        ) {
          asset: assetByAsset {
            id
            url
          }
        }
      }
      member {
        id
        name: given_name
        surname: family_name
        full_name
        memberAvatar: user_assets(
          where: { assetByAsset: { type: { _eq: AVATAR } } }
        ) {
          asset: assetByAsset {
            id
            url
          }
        }
      }
      exam_result {
        id
        result
        score
        completed_at
        assets: exam_result_assets(order_by: { created_at: desc }) {
          id
          created_at
          asset {
            id
            url
            props
          }
        }
      }
      login_sheets(order_by: { created_at: desc }) {
        id
        created_at
        asset {
          id
          url
          props
          type
          createdAt
          updatedAt
        }
      }
      notes(order_by: { created_at: desc }) {
        id
        created_at
        note {
          id
          title
          content
          data
          user
          created_at
          updated_at
          employee {
            id
            full_name
            user_assets(
              where: { assetByAsset: { type: { _eq: AVATAR } } }
              limit: 1
            ) {
              asset: assetByAsset {
                id
                url
              }
            }
          }
        }
      }
      forms(order_by: { created_at: desc }) {
        id
        course
        exam_session_id
        createdAt: created_at
        updatedAt: updated_at
        member
        user
        responsesByResponses {
          id
          complete
          client_complete
          createdAt
          updatedAt
          responses
        }
        formByForm {
          id
          title
          type
          content
          formQuestions(order_by: { order: asc }) {
            id
            order
            properties
            question: questionByQuestion {
              id
              label
              archived
              type
              public
              properties
            }
          }
        }
        userByUser {
          id
          name: given_name
          surname: family_name
          full_name
          email
        }
      }
    }
  }
`;

export const getExamForms = gql`
  query getExamForms {
    forms: form(
      where: {
        _and: [
          { archived: { _eq: false } }
          {
            _or: [
              { type: { _eq: "EXAM" } }
              {
                id: {
                  _in: [
                    "aeec9b90-9e21-4433-9c7f-c5042c32343e"
                    "a297bcf6-2ebc-4bfc-a6e1-378548d4c4b6"
                  ]
                }
              }
            ]
          }
        ]
      }
      order_by: { title: asc }
    ) {
      id
      title
      type
      content
      formQuestions(order_by: { order: asc }) {
        id
        order
        properties
        question: questionByQuestion {
          id
          label
          archived
          type
          public
          properties
        }
      }
    }
  }
`;

export const getBookedExamSlots = gql`
  query getBookedExamSlots($from: timestamptz!, $to: timestamptz!) {
    exam_session(
      where: {
        _and: [
          { status: { _neq: "CANCELLED" } }
          { start: { _gte: $from, _lte: $to } }
        ]
      }
      order_by: { start: asc }
    ) {
      id
      start
      end
      programme_unit_id
      status
      is_itec
      programme {
        itec
      }
    }
  }
`;

export const getExamAttemptCount = gql`
  query getExamAttemptCount($userId: uuid!, $examId: uuid!) {
    examSessions: exam_session(
      where: {
        _and: [
          { exam_id: { _eq: $examId } }
          {
            _or: [
              { user_id: { _eq: $userId } }
              { registration: { user_id: { _eq: $userId } } }
              { member_id: { _eq: $userId } }
            ]
          }
        ]
      }
      order_by: { created_at: asc }
    ) {
      id
      attempt
      counts_as_attempt
      status
      start
      updated_at
      exam_result {
        result
      }
    }
  }
`;

export const insertExamResult = gql`
  mutation insertExamResult($data: exam_result_insert_input!) {
    result: insert_exam_result_one(
      object: $data
      on_conflict: {
        constraint: exam_result_exam_session_id_key
        update_columns: [result, score]
      }
    ) {
      id
    }
  }
`;

export const updateExamResult = gql`
  mutation updateExamResult($id: uuid!, $data: exam_result_set_input!) {
    result: update_exam_result_by_pk(pk_columns: { id: $id }, _set: $data) {
      id
    }
  }
`;

export const getExamResultBySession = gql`
  query getExamResultBySession($examSessionId: uuid!) {
    results: exam_result(
      where: { exam_session_id: { _eq: $examSessionId } }
      limit: 1
    ) {
      id
      result
      score
      completed_at
    }
  }
`;

export const insertExamResultAsset = gql`
  mutation insertExamResultAsset($data: exam_result_asset_insert_input!) {
    examResultAsset: insert_exam_result_asset_one(object: $data) {
      id
      asset {
        id
        url
        props
      }
    }
  }
`;

export const insertExamSessionAsset = gql`
  mutation insertExamSessionAsset($data: exam_session_asset_insert_input!) {
    examSessionAsset: insert_exam_session_asset_one(object: $data) {
      id
      created_at
      asset {
        id
        url
        props
      }
    }
  }
`;

export const deleteExamSessionAsset = gql`
  mutation deleteExamSessionAsset($id: uuid!) {
    examSessionAsset: delete_exam_session_asset_by_pk(id: $id) {
      id
      asset_id
    }
  }
`;

export const insertExamSessionNote = gql`
  mutation insertExamSessionNote($data: exam_session_note_insert_input!) {
    examSessionNote: insert_exam_session_note_one(object: $data) {
      id
      note {
        id
      }
    }
  }
`;

export const deleteExamSessionNote = gql`
  mutation deleteExamSessionNote($id: uuid!) {
    examSessionNote: delete_exam_session_note_by_pk(id: $id) {
      id
      note_id
    }
  }
`;

export const updateExamNote = gql`
  mutation updateExamNote($id: uuid!, $data: note_set_input!) {
    note: update_note_by_pk(pk_columns: { id: $id }, _set: $data) {
      id
      updated_at
    }
  }
`;

export const detachExamForm = gql`
  mutation detachExamForm($id: uuid!) {
    userForm: update_user_form_by_pk(
      pk_columns: { id: $id }
      _set: { exam_session_id: null }
    ) {
      id
    }
  }
`;

// Basic event information query
export const getEventBasicInfo = gql`
  query getEventBasicInfo($id: uuid!) {
    event: event_by_pk(id: $id) {
      id
      status
      updated_at
      start
      seats
      vtct
      vtctSettings
      seat_price
      end
      detail_id
      date
      type
      created_at
      resource_id
      cohorts {
        id
      }
      resource: event_resource {
        id
        name
      }
      detail {
        created_at
        metadata
        attributes
        id
        requires_prescription
        standard_price
        title
        updated_at
        standard_seat
        programme_id
        programme {
          title
          units {
            id
            title
          }
        }
      }
    }
  }
`;

// Event video calls query
export const getEventVideoCalls = gql`
  query getEventVideoCalls($id: uuid!) {
    event: event_by_pk(id: $id) {
      id
      video_calls(where: { status: { _eq: "confirmed" } }) {
        id
        created_at
        updated_at
        url
        meet_id
        status
        start
        end
        data
      }
    }
  }
`;

// Event sessions query
export const getEventSessions = gql`
  query getEventSessions($id: uuid!) {
    event: event_by_pk(id: $id) {
      id
      sessions(
        where: { archived_at: { _is_null: true } }
        order_by: { start: asc }
      ) {
        updated_at
        seat_price
        start
        seats
        type
        product
        product_description
        treatment_id
        prescription
        treatment {
          id
          prescription
        }
        membership_access
        membership_eligible
        membership_member_release_at
        membership_public_release_at
        membership_max_member_spaces
        membership_max_public_spaces
        membership_last_minute_hours
        membership_bypass_booking_limit
        membership_segment_tag
        membership_model_criteria
        membership_screening_required
        model_criteria_structured
        id
        end
        auto
        date
        created_at
        event_id
        registration
        updated_by
        created_by
        updatedBy {
          id
          created_at
          name: given_name
          surname: family_name
        }
        createdBy {
          id
          created_at
          name: given_name
          surname: family_name
        }
        registrations_aggregate(
          where: { type: { _neq: "CANCELLED" }, status: { _neq: "CANCELLED" } }
        ) {
          aggregate {
            count
          }
        }
      }
    }
  }
`;

// Event resources query
export const getEventResources = gql`
  query getEventResources($id: uuid!) {
    event: event_by_pk(id: $id) {
      id
      event_resources(order_by: { created_at: asc }) {
        user_id
        type
        id
        event_id
        user {
          given_name
          family_name
          email
          user_assets(
            limit: 1
            order_by: { assetByAsset: { createdAt: desc } }
          ) {
            assetByAsset {
              url
            }
          }
        }
      }
    }
  }
`;

// Event notes query
export const getEventNotes = gql`
  query getEventNotes($id: uuid!) {
    event: event_by_pk(id: $id) {
      id
      event_notes {
        data: noteByNote {
          created_at
          content
          user
          title
          updated_at
          id
        }
        note
        id
        created_at
        event
        updated_at
      }
    }
  }
`;

// Student registrations query
export const getEventStudentRegistrations = gql`
  query getEventStudentRegistrations($id: uuid!) {
    event: event_by_pk(id: $id) {
      id
      countStudentRegistrations: registrations_aggregate(
        where: {
          _and: {
            event_id: { _eq: $id }
            status: { _neq: "CANCELLED" }
            type: { _eq: "STUDENT" }
          }
        }
      ) {
        aggregate {
          count
        }
      }
      studentRegistrations: registrations(
        where: {
          _and: {
            event_id: { _eq: $id }
            status: { _neq: "CANCELLED" }
            type: { _eq: "STUDENT" }
          }
        }
      ) {
        id
        session_id
        status
        type
        updated_at
        user_id
        student_id
        event_id
        created_at
        enrollment_id
        exam_sessions {
          id
          created_at
          updated_at
          meet_id
          status
          start
          end
          data
          session
          member {
            id
            full_name
            email
            given_name
            user_assets {
              asset: assetByAsset {
                id
                url
              }
            }
          }
        }
        enrollment {
          id
          balance
          status
          total
          client_data
          metadata
          deposit
        }
        user {
          created_at
          name: given_name
          surname: family_name
          given_name
          family_name
          email
          id
          contact_number
          cohort_members(where: { event_id: { _eq: $id } }) {
            id
          }
          labels: user_labels {
            id
            description
            color
            label {
              id
              name
            }
          }
          user_forms(order_by: { created_at: desc }) {
            id
            course
            session_id
            created_at
            responsesByResponses {
              id
              asset
              complete
              responses
              reports {
                reportId: model_report_id
              }
            }
            form: formByForm {
              id
              title
              content
              formQuestions(order_by: { order: asc }) {
                id
                order
                properties
                archived
                question: questionByQuestion {
                  id
                  label
                  archived
                  type
                  public
                  properties
                }
              }
            }
            formByForm {
              id
              title
              content
              formQuestions(order_by: { order: asc }) {
                id
                order
                properties
                archived
                question: questionByQuestion {
                  id
                  label
                  type
                  public
                  archived
                  properties
                }
              }
            }
          }
          certificates {
            event_id
            id
            created_at
          }
        }
        invoices {
          id
          invoice_id
          amount
          status
        }
        payments {
          payment {
            created_at
            amount
            discount
            discount_id
            discount_explanation
            method
          }
        }
      }
    }
  }
`;

// Model registrations query
export const getEventModelRegistrations = gql`
  query getEventModelRegistrations($id: uuid!) {
    event: event_by_pk(id: $id) {
      id
      countModelRegistrations: registrations_aggregate(
        where: {
          _and: {
            event_id: { _eq: $id }
            status: { _neq: "CANCELLED" }
            _or: [{ type: { _eq: "MODEL" } }, { type: { _eq: "NO_SHOW" } }]
          }
        }
      ) {
        aggregate {
          count
        }
      }
      modelRegistrations: registrations(
        where: {
          _and: {
            event_id: { _eq: $id }
            status: { _neq: "CANCELLED" }
            _or: [{ type: { _eq: "MODEL" } }, { type: { _eq: "NO_SHOW" } }]
          }
        }
      ) {
        id
        session_id
        status
        type
        confirmed
        confirmed_by
        confirmed_at
        follow_up
        updated_at
        user_id
        event_id
        created_at
        student_id
        confirmer {
          full_name
          email
          given_name
          user_assets {
            asset: assetByAsset {
              id
              url
            }
          }
        }
        model_session_purchase {
          id
          status
          total
          line_items
          employee {
            full_name
            email
            given_name
            user_assets {
              asset: assetByAsset {
                id
                url
              }
            }
          }
        }
        modelReports {
          status
          id
          student_id
          student {
            id
            name: given_name
            surname: family_name
          }
          created_at
          updated_at
          assets {
            id
            asset {
              props
              url
            }
          }
        }
        model_feedback {
          id
          created_at
        }
        user {
          created_at
          given_name
          family_name
          name: given_name
          surname: family_name
          email
          contact_number
          id
          labels: user_labels {
            id
            description
            color
            label {
              id
              name
            }
          }
          user_forms {
            id
            course
            session_id
            created_at
            responsesByResponses {
              id
              responses
              asset
              complete
              completed_by_employee
              completed_at
              client_complete
              client_completed_at
              reports {
                reportId: model_report_id
              }
            }
            formByForm {
              id
              title
              content
            }
          }
          memberships(
            where: { status: { _in: ["ACTIVE", "SUSPENDED"] } }
            limit: 1
          ) {
            id
            status
            plan {
              name
            }
          }
        }
        payments {
          payment {
            created_at
            amount
            discount
            discount_id
            discount_explanation
            method
          }
        }
      }
    }
  }
`;

export const getFormQuestions = gql`
  query getFormQuestions($id: uuid!) {
    formQuestions: form_question(where: { formByForm: { id: { _eq: $id } } }) {
      id
      order
      properties
      archived
      question: questionByQuestion {
        id
        label
        type
        archived
        public
        properties
      }
    }
  }
`;
